import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Tabs, Table, Button, Select, Row, Col, Space, Tag,
  Alert, message, Modal, Tooltip, Typography, Divider, Badge,
} from 'antd';
import {
  SwapOutlined, UserDeleteOutlined, WarningOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import apiClient from '../../../../api/apiClient';
import { getCicloActual, getAniosAcademicos } from '../../../../utils/cicloEscolar';
import ReasignacionIndividualModal from './components/ReasignacionIndividualModal';

const { Option } = Select;
const { Text, Title } = Typography;

// Normaliza la respuesta de /asignaciones (puede venir en varios formatos)
const normalizarAsignaciones = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) {
    if (data.length === 0) return [];
    if (data[0]?.IdAsignacionDocente) return data;
    const primero = data[0];
    return Object.keys(primero)
      .filter((k) => !isNaN(k))
      .map((k) => primero[k])
      .filter((item) => item?.IdAsignacionDocente);
  }
  if (typeof data === 'object' && data.IdAsignacionDocente) return [data];
  return [];
};

const ReasignacionCursos = () => {
  const [tabActiva, setTabActiva] = useState('individual');

  // ─── Catálogos compartidos ─────────────────────────────────────────────────
  const [docentes, setDocentes] = useState([]);
  const [grados, setGrados] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [jornadas, setJornadas] = useState([]);

  // ─── Tab Individual ────────────────────────────────────────────────────────
  const [asignaciones, setAsignaciones] = useState([]);
  const [loadingTabla, setLoadingTabla] = useState(false);
  const [filtros, setFiltros] = useState({
    anio: getCicloActual(),
    idGrado: null,
    idSeccion: null,
    idJornada: null,
    idDocente: null,
  });
  const [modalIndividual, setModalIndividual] = useState({ visible: false, asignacion: null });

  // ─── Tab Masiva ────────────────────────────────────────────────────────────
  const [docenteSaliente, setDocenteSaliente] = useState(null);
  const [asignacionesSaliente, setAsignacionesSaliente] = useState([]);
  const [loadingSaliente, setLoadingSaliente] = useState(false);
  const [reemplazos, setReemplazos] = useState({}); // { [IdAsignacion]: IdDocenteNuevo }
  const [loadingMasiva, setLoadingMasiva] = useState(false);
  const [masivaExitosa, setMasivaExitosa] = useState(false);
  const [loadingBaja, setLoadingBaja] = useState(false);

  // ─── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    cargarCatalogos();
  }, []);

  useEffect(() => {
    if (tabActiva === 'individual') cargarAsignaciones();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, tabActiva]);

  const cargarCatalogos = async () => {
    try {
      const [docentesRes, gradosRes, seccionesRes, jornadasRes] = await Promise.all([
        apiClient.get('/docentes'),
        apiClient.get('/grados'),
        apiClient.get('/secciones'),
        apiClient.get('/jornadas'),
      ]);
      if (docentesRes.data.success) setDocentes(docentesRes.data.data);
      if (gradosRes.data.success) setGrados(gradosRes.data.data);
      if (seccionesRes.data.success || seccionesRes.data.data) setSecciones(seccionesRes.data.data || []);
      if (jornadasRes.data.success || jornadasRes.data.data) setJornadas(jornadasRes.data.data || []);
    } catch {
      message.error('Error al cargar catálogos');
    }
  };

  // ─── Tab Individual ────────────────────────────────────────────────────────
  const cargarAsignaciones = useCallback(async () => {
    setLoadingTabla(true);
    try {
      const params = {};
      if (filtros.anio) params.anio = filtros.anio;
      if (filtros.idGrado) params.idGrado = filtros.idGrado;
      if (filtros.idSeccion) params.idSeccion = filtros.idSeccion;
      if (filtros.idJornada) params.idJornada = filtros.idJornada;
      if (filtros.idDocente) params.idDocente = filtros.idDocente;

      const response = await apiClient.get('/asignaciones', { params });
      setAsignaciones(
        response.data.success ? normalizarAsignaciones(response.data.data) : []
      );
    } catch {
      message.error('Error al cargar asignaciones');
      setAsignaciones([]);
    } finally {
      setLoadingTabla(false);
    }
  }, [filtros]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const columnsIndividual = [
    { title: 'Año', dataIndex: 'Anio', key: 'Anio', width: 80, render: (v) => <Tag color="blue">{v}</Tag> },
    { title: 'Docente actual', dataIndex: 'NombreDocente', key: 'NombreDocente', width: 200 },
    { title: 'Curso', dataIndex: 'NombreCurso', key: 'NombreCurso', width: 180 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'NombreGrado', width: 140 },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'NombreSeccion', width: 90, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'NombreJornada', width: 110 },
    {
      title: 'Estado', dataIndex: 'Estado', key: 'Estado', width: 90, align: 'center',
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Activo' : 'Inactivo'}</Tag>,
    },
    {
      title: 'Acciones', key: 'acciones', width: 120, fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Cambiar el docente de esta asignación">
          <Button
            type="primary"
            icon={<SwapOutlined />}
            size="small"
            onClick={() => setModalIndividual({ visible: true, asignacion: record })}
            disabled={!record.Estado}
          >
            Reasignar
          </Button>
        </Tooltip>
      ),
    },
  ];

  // ─── Tab Masiva ────────────────────────────────────────────────────────────
  const cargarAsignacionesSaliente = async (idDocente) => {
    setLoadingSaliente(true);
    setAsignacionesSaliente([]);
    setReemplazos({});
    setMasivaExitosa(false);
    try {
      const response = await apiClient.get('/asignaciones', { params: { idDocente } });
      const data = response.data.success ? normalizarAsignaciones(response.data.data) : [];
      const activas = data.filter((a) => a.Estado);
      setAsignacionesSaliente(activas);
    } catch {
      message.error('Error al cargar asignaciones del docente');
    } finally {
      setLoadingSaliente(false);
    }
  };

  const handleDocenteSalienteChange = (idDocente) => {
    setDocenteSaliente(idDocente);
    if (idDocente) cargarAsignacionesSaliente(idDocente);
    else {
      setAsignacionesSaliente([]);
      setReemplazos({});
      setMasivaExitosa(false);
    }
  };

  const handleReemplazoChange = (idAsignacion, idDocenteNuevo) => {
    setReemplazos((prev) => ({ ...prev, [idAsignacion]: idDocenteNuevo }));
  };

  const todasCubiertas =
    asignacionesSaliente.length > 0 &&
    asignacionesSaliente.every((a) => reemplazos[a.IdAsignacionDocente]);

  const handleReasignarMasivo = async () => {
    if (!todasCubiertas) {
      message.warning('Debes asignar un docente de reemplazo a cada curso antes de continuar.');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user')) || {};
    const payload = {
      IdDocenteSaliente: docenteSaliente,
      Reasignaciones: asignacionesSaliente.map((a) => ({
        IdAsignacion: a.IdAsignacionDocente,
        IdDocenteNuevo: reemplazos[a.IdAsignacionDocente],
      })),
      ModificadoPor: String(user.IdUsuario || ''),
    };

    setLoadingMasiva(true);
    try {
      const response = await apiClient.post('/asignaciones/reasignar-docente-masivo', payload);
      if (response.data.success) {
        message.success(response.data.message || 'Reasignación masiva completada');
        setMasivaExitosa(true);
        setAsignacionesSaliente([]);
      }
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.asignacionesPendientes) {
        const ids = data.asignacionesPendientes.join(', ');
        message.error(`Faltan asignaciones por cubrir: IDs ${ids}`);
      } else {
        message.error(data?.error || 'Error al reasignar');
      }
    } finally {
      setLoadingMasiva(false);
    }
  };

  const handleDarDeBaja = () => {
    const docente = docentes.find((d) => d.idDocente === docenteSaliente);
    const nombre = docente?.NombreDocente || `ID ${docenteSaliente}`;
    Modal.confirm({
      title: `¿Dar de baja a ${nombre}?`,
      icon: <WarningOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <p>El docente ya no tiene asignaciones activas.</p>
          <p>Esta acción lo marcará como inactivo en el sistema.</p>
        </div>
      ),
      okText: 'Sí, dar de baja',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        setLoadingBaja(true);
        try {
          const user = JSON.parse(localStorage.getItem('user')) || {};
          await apiClient.delete(`/docentes/${docenteSaliente}`, {
            data: { ModificadoPor: String(user.IdUsuario || '') },
          });
          message.success(`${nombre} dado de baja exitosamente`);
          setDocenteSaliente(null);
          setMasivaExitosa(false);
          setReemplazos({});
          cargarCatalogos();
        } catch (error) {
          message.error(error.response?.data?.error || 'Error al dar de baja al docente');
        } finally {
          setLoadingBaja(false);
        }
      },
    });
  };

  const docentesReemplazoDisponibles = (idAsignacion) => {
    const asignacion = asignacionesSaliente.find((a) => a.IdAsignacionDocente === idAsignacion);
    return docentes.filter((d) => d.idDocente !== docenteSaliente && d.idDocente !== asignacion?.IdDocente);
  };

  const columnsMasiva = [
    { title: 'Curso', dataIndex: 'NombreCurso', key: 'NombreCurso', width: 180 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'NombreGrado', width: 140 },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'NombreSeccion', width: 90, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'NombreJornada', width: 110 },
    { title: 'Año', dataIndex: 'Anio', key: 'Anio', width: 80, render: (v) => <Tag color="blue">{v}</Tag> },
    {
      title: 'Docente de reemplazo',
      key: 'reemplazo',
      render: (_, record) => (
        <Select
          placeholder="Selecciona reemplazo"
          style={{ width: '100%', minWidth: 220 }}
          showSearch
          optionFilterProp="children"
          value={reemplazos[record.IdAsignacionDocente] || undefined}
          onChange={(val) => handleReemplazoChange(record.IdAsignacionDocente, val)}
          status={!reemplazos[record.IdAsignacionDocente] ? 'warning' : ''}
        >
          {docentesReemplazoDisponibles(record.IdAsignacionDocente).map((d) => (
            <Option key={d.idDocente} value={d.idDocente}>
              {d.NombreDocente}
            </Option>
          ))}
        </Select>
      ),
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'individual',
      label: (
        <span>
          <SwapOutlined /> Reasignación Individual
        </span>
      ),
      children: (
        <div>
          <Alert
            type="info"
            showIcon
            message="Usa esta opción para cambiar el docente de una asignación específica sin afectar el resto de sus cursos."
            style={{ marginBottom: 16 }}
          />

          {/* Filtros */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={4}>
              <Select placeholder="Año" style={{ width: '100%' }} value={filtros.anio}
                onChange={(v) => handleFiltroChange('anio', v)} allowClear>
                {getAniosAcademicos().map((y) => (
                  <Option key={y} value={y}>{y}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select placeholder="Grado" style={{ width: '100%' }} value={filtros.idGrado}
                onChange={(v) => handleFiltroChange('idGrado', v)} allowClear showSearch optionFilterProp="children">
                {grados.map((g) => <Option key={g.IdGrado} value={g.IdGrado}>{g.NombreGrado}</Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select placeholder="Sección" style={{ width: '100%' }} value={filtros.idSeccion}
                onChange={(v) => handleFiltroChange('idSeccion', v)} allowClear>
                {secciones.map((s) => <Option key={s.IdSeccion} value={s.IdSeccion}>{s.NombreSeccion}</Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select placeholder="Jornada" style={{ width: '100%' }} value={filtros.idJornada}
                onChange={(v) => handleFiltroChange('idJornada', v)} allowClear>
                {jornadas.map((j) => <Option key={j.IdJornada} value={j.IdJornada}>{j.NombreJornada}</Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select placeholder="Docente" style={{ width: '100%' }} value={filtros.idDocente}
                onChange={(v) => handleFiltroChange('idDocente', v)} allowClear showSearch optionFilterProp="children">
                {docentes.map((d) => <Option key={d.idDocente} value={d.idDocente}>{d.NombreDocente}</Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={2}>
              <Button style={{ width: '100%' }}
                onClick={() => setFiltros({ anio: null, idGrado: null, idSeccion: null, idJornada: null, idDocente: null })}>
                Limpiar
              </Button>
            </Col>
          </Row>

          <Table
            columns={columnsIndividual}
            dataSource={asignaciones}
            rowKey="IdAsignacionDocente"
            loading={loadingTabla}
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total: ${t} asignaciones` }}
            bordered
            size="middle"
            scroll={{ x: 1000 }}
          />

          <ReasignacionIndividualModal
            visible={modalIndividual.visible}
            asignacion={modalIndividual.asignacion}
            docentes={docentes}
            onCancel={() => setModalIndividual({ visible: false, asignacion: null })}
            onSuccess={() => {
              setModalIndividual({ visible: false, asignacion: null });
              cargarAsignaciones();
            }}
          />
        </div>
      ),
    },
    {
      key: 'masiva',
      label: (
        <span>
          <UserDeleteOutlined /> Baja de Docente
        </span>
      ),
      children: (
        <div>
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message="Usa esta opción cuando un docente se retira. Debes reasignar TODOS sus cursos activos antes de poder dar la baja."
            style={{ marginBottom: 20 }}
          />

          {/* Paso 1 — Seleccionar docente saliente */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row align="middle" gutter={16}>
              <Col flex="none">
                <Text strong>Paso 1 — Docente que se retira:</Text>
              </Col>
              <Col flex="auto">
                <Select
                  placeholder="Selecciona el docente"
                  style={{ width: '100%', maxWidth: 360 }}
                  showSearch
                  optionFilterProp="children"
                  value={docenteSaliente}
                  onChange={handleDocenteSalienteChange}
                  allowClear
                  loading={loadingSaliente}
                >
                  {docentes.map((d) => (
                    <Option key={d.idDocente} value={d.idDocente}>
                      {d.NombreDocente}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Card>

          {/* Sin asignaciones activas */}
          {docenteSaliente && !loadingSaliente && asignacionesSaliente.length === 0 && !masivaExitosa && (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Este docente no tiene asignaciones activas."
              description="Puedes proceder directamente a dar la baja."
              style={{ marginBottom: 16 }}
              action={
                <Button danger type="primary" icon={<UserDeleteOutlined />}
                  loading={loadingBaja} onClick={handleDarDeBaja}>
                  Dar de Baja
                </Button>
              }
            />
          )}

          {/* Paso 2 — Tabla de reemplazos */}
          {asignacionesSaliente.length > 0 && (
            <>
              <Card size="small" style={{ marginBottom: 12 }}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text strong>Paso 2 — Asigna un reemplazo para cada curso:</Text>
                    <Text type="secondary" style={{ marginLeft: 12 }}>
                      <Badge
                        count={`${Object.keys(reemplazos).length} / ${asignacionesSaliente.length}`}
                        style={{ backgroundColor: todasCubiertas ? '#52c41a' : '#faad14' }}
                      />
                    </Text>
                  </Col>
                </Row>
              </Card>

              <Table
                columns={columnsMasiva}
                dataSource={asignacionesSaliente}
                rowKey="IdAsignacionDocente"
                loading={loadingSaliente}
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: 800 }}
                style={{ marginBottom: 16 }}
                rowClassName={(record) =>
                  !reemplazos[record.IdAsignacionDocente] ? 'row-pendiente' : ''
                }
              />

              <Divider />

              <Row justify="end">
                <Button
                  type="primary"
                  icon={<SwapOutlined />}
                  size="large"
                  loading={loadingMasiva}
                  disabled={!todasCubiertas}
                  onClick={handleReasignarMasivo}
                >
                  Reasignar Todos los Cursos
                </Button>
              </Row>
            </>
          )}

          {/* Reasignación exitosa — ofrecer baja */}
          {masivaExitosa && (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Reasignación masiva completada"
              description="Todos los cursos fueron transferidos. Ahora puedes dar de baja al docente de forma segura."
              style={{ marginTop: 20 }}
              action={
                <Space direction="vertical">
                  <Button
                    danger
                    type="primary"
                    icon={<UserDeleteOutlined />}
                    loading={loadingBaja}
                    onClick={handleDarDeBaja}
                  >
                    Dar de Baja al Docente
                  </Button>
                </Space>
              }
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <SwapOutlined style={{ color: '#1677ff' }} />
            <Title level={5} style={{ margin: 0 }}>Reasignación de Cursos</Title>
          </Space>
        }
      >
        <Tabs activeKey={tabActiva} onChange={setTabActiva} items={tabItems} />
      </Card>
    </div>
  );
};

export default ReasignacionCursos;
